import React, { useEffect, useMemo, useState } from 'react';
import { Avatar, Modal, Select, Spin, message } from 'antd';
import { LinkOutlined, TeamOutlined, UserOutlined } from '@ant-design/icons';
import QR_STYLES, { QR_COLORS, QR_PATTERNS } from 'constants/invitationQrStyles';
import { createInvitationQrDataUrl } from 'helpers/invitationQr';

export default function InviteLinkModal({ open, onClose, targetType, target, title, targetAvatar, t }) {
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [qr, setQr] = useState('');
  const [qrLoading, setQrLoading] = useState(false);
  const style = useMemo(() => {
    const legacyStyle = QR_STYLES[invitation?.qr_style] || QR_STYLES.midnight;
    return {
      ...(QR_PATTERNS[invitation?.qr_pattern] || legacyStyle),
      ...(QR_COLORS[invitation?.qr_color] || legacyStyle),
    };
  }, [invitation?.qr_color, invitation?.qr_pattern, invitation?.qr_style]);
  const url = useMemo(
    () => (invitation && typeof window !== 'undefined' ? `${window.location.origin}${invitation.url_path}` : ''),
    [invitation]
  );

  useEffect(() => {
    if (!open || !target) return;
    setInvitation(null);
    setQr('');
    setLoading(true);
    fetch('/api/invitations', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ target_type: targetType, target }),
    })
      .then(async (response) => {
        const payload = await response.json();
        if (!response.ok) throw new Error(payload?.message || 'Не удалось создать приглашение');
        return payload;
      })
      .then((payload) => setInvitation(payload.data))
      .catch((error) => message.error(error.message))
      .finally(() => setLoading(false));
  }, [open, target, targetType]);

  useEffect(() => {
    if (!url) return;
    setQrLoading(true);
    createInvitationQrDataUrl(url, style, targetAvatar || (targetType === 'chat_room' ? '/images/logo.svg' : null))
      .then(setQr)
      .catch(() => message.error('Не удалось создать QR-код'))
      .finally(() => setQrLoading(false));
  }, [url, style, targetAvatar, targetType]);

  const setQrOption = (field, value) => {
    setQrLoading(true);
    setInvitation((current) => ({ ...current, [field]: value }));
    fetch(`/api/invitations/${invitation.code}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ [field]: value }),
    }).catch(() => message.error('Не удалось сохранить оформление QR-кода'));
  };

  return (
    <Modal open={open} onCancel={onClose} footer={null} title={<><LinkOutlined /> Приглашение</>}>
      {loading ? <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div> : invitation ? (
        <div style={{ textAlign: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, color: '#627d98', marginBottom: 16 }}>
            {targetAvatar ? (
              <img src={targetAvatar} alt='' style={{ width: 42, height: 42, borderRadius: '50%', objectFit: 'cover' }} />
            ) : (
              <Avatar size={42} icon={targetType === 'chat_room' ? <TeamOutlined /> : <UserOutlined />} style={{ background: '#e6f4ff', color: '#1677ff' }} />
            )}
            <span>Ссылка {title ? `в «${title}»` : 'для приглашения'}</span>
          </div>
          <div style={{ position: 'relative', width: 230, maxWidth: '100%', minHeight: 230, margin: '0 auto', borderRadius: 12, background: style.light }}>
            {qr && <img src={qr} alt='QR-код приглашения' style={{ width: '100%', display: 'block', borderRadius: 12, padding: 10, opacity: qrLoading ? 0.25 : 1, transition: 'opacity 160ms ease' }} />}
            {qrLoading && (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 12, background: 'rgba(16, 42, 67, 0.42)' }}>
                <Spin size='large' />
              </div>
            )}
          </div>
          <div style={{ margin: '14px 0 8px', color: '#627d98', fontSize: 12 }}>Форма QR-кода</div>
          <Select
            value={invitation.qr_pattern || 'square'}
            onChange={(value) => setQrOption('qr_pattern', value)}
            options={Object.entries(QR_PATTERNS).map(([value, item]) => ({ value, label: item.label }))}
            style={{ width: '100%', maxWidth: 260 }}
          />
          <div style={{ margin: '14px 0 8px', color: '#627d98', fontSize: 12 }}>Цвет QR-кода</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 8 }}>
            {Object.entries(QR_COLORS).map(([key, value]) => (
              <button
                key={key}
                type='button'
                title={value.label}
                onClick={() => setQrOption('qr_color', key)}
                style={{ width: 24, height: 24, borderRadius: '50%', border: invitation.qr_color === key ? '3px solid white' : '1px solid white', outline: invitation.qr_color === key ? '2px solid #102a43' : '1px solid #d9e2ec', background: value.dark, cursor: 'pointer' }}
              />
            ))}
          </div>
          <a
            href={url}
            target='_blank'
            rel='noreferrer'
            style={{ display: 'block', marginTop: 14, color: 'rgb(103, 162, 184)', overflowWrap: 'anywhere' }}
          >
            {url}
          </a>
          <div style={{ marginTop: 12, color: '#000', fontFamily: 'monospace', fontSize: 22, fontWeight: 700, letterSpacing: 5 }}>{invitation.code}</div>
        </div>
      ) : null}
    </Modal>
  );
}
